import axios from 'axios';
import config from '../environment/config';
import { authenticationDetails } from './admin.service';
import { authHeader } from './auth.header';


const API_URL = config.API_URL;

// ─── Token Refresh State ────────────────────────────────────────────────────────
// Prevents multiple concurrent refresh attempts. When a refresh is in progress,
// all subsequent 401 intercepted requests wait for the same refresh promise.
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

// ─── Silent Token Refresh ───────────────────────────────────────────────────────
// Calls the backend /refreshtoken endpoint with the expired JWT.
// The backend filter recognises the isRefreshToken header and allows the
// expired token through, generates a fresh JWT (24-hour expiry), and returns it.
export const refreshAccessToken = async () => {
  const user = getCurrentUser();
  if (!user?.token) throw new Error('No token to refresh');

  const response = await axios.get(`${API_URL}refreshtoken`, {
    headers: {
      Authorization: `Bearer ${user.token}`,
      isRefreshToken: 'true',
      'Content-Type': 'application/json',
    },
  });

  const newToken = response.data?.token;
  if (!newToken) throw new Error('No token in refresh response');

  // Update stored credentials
  localStorage.setItem(
    'user',
    JSON.stringify({ token: newToken, username: user.username })
  );

  return newToken;
};


export const login = async (username, password) => {
  try {
    let encryptedPassword = btoa(password);

    // Add timestamp to prevent replay attacks
    const timestamp = Date.now();

    const loginData = {
      username: username.trim(),
      password: encryptedPassword,
      timestamp: timestamp
    };

    const response = await authenticationDetails(loginData);

    if (response.data.data.token) {
      localStorage.setItem(
        'user',
        JSON.stringify({
          token: response.data.data.token,
          username
        })
      );
      const emp = await getUserDetails(username);

      localStorage.setItem('loginId', emp.loginId);
      localStorage.setItem('empName', emp.name);
      localStorage.setItem('roleId', emp.roleId);
      localStorage.setItem('roleName', emp.roleName);
      localStorage.setItem('username', emp.username);


      return response.data;
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (error) {
    console.error('Error occurred in login:', error);
    throw error;
  }
};

export const logout = async () => {
  const user = getCurrentUser();
  const keysToRemove = [
    'loginId', 'roleId', 'roleName', 'empName', 'user', 'username'
  ];
  if (user && user.username) {
    try {
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error occurred in logout:', error);
      throw error;
    }
  } else {
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
};

export const logoutUser = async () => {
  try {
    return (await axios.post(
      `${API_URL}api/auth/logout`,
      {},
      { headers: { 'Content-Type': 'application/json', ...authHeader() } }
    )).data;
  } catch (error) {
    console.error('logoutUser error:', error);
    // Clean local storage even if backend fails
    return { success: true };
  }
};

export const reportSessionExpired = async (username) => {
  try {
    const resolvedUser = (username && typeof username === 'string' && username.trim() && username !== 'null' && username !== 'undefined')
      ? username.trim()
      : localStorage.getItem("username");

    const query = resolvedUser ? `?username=${encodeURIComponent(resolvedUser)}` : '';
    return (await axios.post(
      `${API_URL}api/auth/session-expired${query}`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          ...authHeader()
        }
      }
    )).data;
  } catch (error) {
    console.error('reportSessionExpired error:', error);
  }
};

export const getCurrentUser = () => {
  return JSON.parse(localStorage.getItem('user'));
};

export const getUserDetails = async (username) => {
  if (!username) {
    throw new Error('No user found');
  } try {
    return (await axios.get(`${API_URL}get-user-details`, { headers: { 'Content-Type': 'application/json', ...authHeader() } })).data;
  } catch (error) {
    console.error('Error occurred in getEmpDetails():', error);
    throw error;
  }
};


export const forgotPassword = async (emailOrMobile) => {
  try {
    const response = await axios.post(`${API_URL}forgot-password`, {
      emailOrMobile: emailOrMobile.trim(),
    }, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error('Error occurred in forgotPassword():', error);
    throw error;
  }
};

export const verifyOtp = async (usernameOrEmail, otp) => {
  try {
    const response = await axios.post(`${API_URL}verify-otp`, {
      usernameOrEmail: usernameOrEmail.trim(),
      otp: otp.trim(),
    }, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error('Error occurred in verifyOtp():', error);
    throw error;
  }
};

export const resetPassword = async (usernameOrEmail, otp, newPassword, confirmPassword) => {
  try {
    const response = await axios.post(`${API_URL}reset-password`, {
      usernameOrEmail: usernameOrEmail.trim(),
      otp: otp.trim(),
      newPassword: btoa(newPassword),
      confirmPassword: btoa(confirmPassword),
    }, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error('Error occurred in resetPassword():', error);
    throw error;
  }
};

export const insertSignUpDetails = async (values) => {
  try {
    return (await axios.post(`${API_URL}insert-signup`, values, { headers: { 'Content-Type': 'application/json' } })).data;
  } catch (error) {
    console.error('Error occurred in insertSignUpDetails():', error);
    throw error;
  }
};


let isSessionExpiredModalOpen = false;
export const handleSessionExpired = async () => {
  if (isSessionExpiredModalOpen) return;
  isSessionExpiredModalOpen = true;

  // Resolve username BEFORE clearing credentials from localStorage
  const username = localStorage.getItem("username");

  // Report the expiry without delaying the user-facing session-expired flow.
  void reportSessionExpired(username);

  // Preserve the current path so the user returns to their work after re-authenticating
  try {
    const currentPath = window.location.pathname;
    if (currentPath && currentPath !== "/login" && currentPath !== "/" && currentPath !== "/sign-up") {
      sessionStorage.setItem("redirectAfterLogin", currentPath + window.location.search);
    }
  } catch (err) {
    console.warn("Could not save redirect path:", err);
  }

  // Clear credentials before redirecting so parallel 401 responses are ignored.
  localStorage.removeItem('loginId');
  localStorage.removeItem('empName');
  localStorage.removeItem('roleId');
  localStorage.removeItem('roleName');
  localStorage.removeItem('username');
  localStorage.removeItem('user');

  window.location.replace('/login?sessionExpired=true');
};

// ─── Global Axios Response Interceptor ──────────────────────────────────────────
// Industry standard: On 401, silently attempt token refresh before forcing re-login.
// If the refresh succeeds, the original request is retried transparently.
// If the refresh also fails (refresh token expired / revoked), the user is
// redirected to the login page with a "Session Expired" notification.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const url = originalRequest?.url || '';

    // Do not intercept login, logout, session-expired, or refresh calls
    const isExcluded =
      url.includes('/authenticate') ||
      url.includes('/api/auth/session-expired') ||
      url.includes('/api/auth/logout') ||
      url.includes('/refreshtoken');

    if (status === 401 && !isExcluded && !originalRequest._retry) {
      // Check if user was previously authenticated
      let hasAuthenticatedUser = false;
      try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        hasAuthenticatedUser = Boolean(user?.token);
      } catch (parseError) {
        console.error('Unable to read the current authenticated user:', parseError);
      }

      if (!hasAuthenticatedUser) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request and wait
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(axios(originalRequest));
          });
        });
      }

      // Start refresh
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;

        // Notify all queued requests with the new token
        onRefreshed(newToken);

        // Retry the original request with the new token
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return axios(originalRequest);

      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        console.error('Token refresh failed, session expired:', refreshError);

        // Refresh failed — session is truly expired, redirect to login
        await handleSessionExpired();
        return Promise.reject(refreshError);
      }
    }

    // For 403 (account disabled/locked), also handle session expiry
    if (status === 403 && !isExcluded) {
      let hasAuthenticatedUser = false;
      try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        hasAuthenticatedUser = Boolean(user?.token);
      } catch (parseError) {
        console.error('Unable to read the current authenticated user:', parseError);
      }

      if (hasAuthenticatedUser) {
        await handleSessionExpired();
      }
    }

    return Promise.reject(error);
  }
);
